import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { FileText, Search, Filter, CheckCircle2, XCircle, Clock, User, Shield, Activity } from 'lucide-react';

interface AuditLog {
  id: number;
  action: string;
  entityType?: string;
  entityId?: string;
  description?: string;
  userId?: string;
  userRole?: string;
  ipAddress?: string;
  timestamp: string;
  isSuccess: boolean;
}

const T = {
  title: "سجلات التدقيق",
  subtitle: "عرض وتتبع جميع العمليات في النظام",
  totalLogs: "إجمالي السجلات",
  successfulLogs: "العمليات الناجحة",
  failedLogs: "العمليات الفاشلة",
  todayLogs: "اليوم",
  loading: "جاري التحميل...",
  noLogs: "لا توجد سجلات",
  search: "بحث في السجلات...",
  filter: "فلترة",
  allActions: "جميع الإجراءات",
  allStatus: "جميع الحالات",
  success: "ناجح",
  failed: "فاشل",
  action: "الإجراء",
  entityType: "نوع الكيان",
  entityId: "معرف الكيان",
  description: "الوصف",
  user: "المستخدم",
  userRole: "الدور",
  ipAddress: "عنوان IP",
  timestamp: "الوقت",
  status: "الحالة",
  error: "حدث خطأ",
  minutes: "دقيقة",
  hours: "ساعة",
  days: "يوم",
  ago: "مضت"
};

export default function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { loadLogs(); }, []);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/auditlogs?pageSize=100');
      if (res.data?.success) {
        const data = res.data.data;
        setLogs(Array.isArray(data) ? data : (data.items || []));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const filtered = logs.filter(log => {
    const matchSearch = 
      (log.action || '').toLowerCase().includes(search.toLowerCase()) ||
      (log.description || '').toLowerCase().includes(search.toLowerCase()) ||
      (log.entityType || '').toLowerCase().includes(search.toLowerCase()) ||
      (log.userId || '').toLowerCase().includes(search.toLowerCase()) ||
      (log.ipAddress || '').toLowerCase().includes(search.toLowerCase());
    const matchAction = actionFilter === 'all' || log.action === actionFilter;
    const matchStatus = statusFilter === 'all' || 
      (statusFilter === 'success' && log.isSuccess) || 
      (statusFilter === 'failed' && !log.isSuccess);
    return matchSearch && matchAction && matchStatus;
  });

  const successfulCount = logs.filter(l => l.isSuccess).length;
  const failedCount = logs.filter(l => !l.isSuccess).length;
  const todayCount = logs.filter(l => {
    const today = new Date().toDateString();
    return new Date(l.timestamp).toDateString() === today;
  }).length;

  const actions = Array.from(new Set(logs.map(l => l.action).filter(Boolean)));

  const getTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'الآن';
    if (minutes < 60) return `${minutes} ${T.minutes} ${T.ago}`;
    if (hours < 24) return `${hours} ${T.hours} ${T.ago}`;
    return `${days} ${T.days} ${T.ago}`;
  };

  const getActionColor = (action: string) => {
    if (action.includes('Create') || action.includes('Add')) return 'bg-green-100 text-green-700';
    if (action.includes('Update') || action.includes('Edit')) return 'bg-blue-100 text-blue-700';
    if (action.includes('Delete') || action.includes('Remove')) return 'bg-red-100 text-red-700';
    if (action.includes('Login')) return 'bg-purple-100 text-purple-700';
    if (action.includes('Export')) return 'bg-orange-100 text-orange-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl flex items-center justify-center shadow-lg">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <span>{T.title}</span>
            <p className="text-sm text-gray-500 font-normal mt-1">{T.subtitle}</p>
          </div>
        </h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><Activity className="w-5 h-5 text-slate-600" /></div></div>
          <p className="text-sm text-gray-500">{T.totalLogs}</p>
          <p className="text-2xl font-bold text-gray-900">{logs.length}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-green-600" /></div></div>
          <p className="text-sm text-gray-500">{T.successfulLogs}</p>
          <p className="text-2xl font-bold text-green-700">{successfulCount}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center"><XCircle className="w-5 h-5 text-red-600" /></div></div>
          <p className="text-sm text-gray-500">{T.failedLogs}</p>
          <p className="text-2xl font-bold text-red-700">{failedCount}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Clock className="w-5 h-5 text-blue-600" /></div></div>
          <p className="text-sm text-gray-500">{T.todayLogs}</p>
          <p className="text-2xl font-bold text-blue-700">{todayCount}</p>
        </CardContent></Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" placeholder={T.search} value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-slate-500 transition" />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-slate-500">
                <option value="all">{T.allActions}</option>
                {actions.map(action => <option key={action} value={action}>{action}</option>)}
              </select>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-slate-500">
                <option value="all">{T.allStatus}</option>
                <option value="success">{T.success}</option>
                <option value="failed">{T.failed}</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto"></div><p className="text-gray-500 mt-4">{T.loading}</p></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center"><FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-semibold text-gray-900 mb-2">{T.noLogs}</h3></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.action}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.entityType}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.description}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.user}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.ipAddress}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.timestamp}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.status}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((log) => (
                    <tr key={log.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                      <td className="py-4 px-5">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="py-4 px-5">
                        <div>
                          <p className="font-semibold text-gray-900">{log.entityType || '-'}</p>
                          {log.entityId && <p className="text-xs text-gray-500 font-mono">{log.entityId}</p>}
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <p className="text-sm text-gray-700 max-w-md truncate">{log.description || '-'}</p>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{log.userId || 'System'}</p>
                            {log.userRole && <p className="text-xs text-gray-500">{log.userRole}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <p className="text-sm text-gray-700 font-mono">{log.ipAddress || '-'}</p>
                      </td>
                      <td className="py-4 px-5">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{new Date(log.timestamp).toLocaleString('ar-EG')}</p>
                          <p className="text-xs text-gray-500">{getTimeAgo(log.timestamp)}</p>
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${log.isSuccess ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                          {log.isSuccess ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                          {log.isSuccess ? T.success : T.failed}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
